import {
	type DefaultError,
	type QueryKey,
	type UseInfiniteQueryOptions,
	type UseMutationOptions,
	type UseQueryOptions,
	type InfiniteData,
	type MutationKey,
	useQuery as _useQuery,
	useInfiniteQuery as _useInfiniteQuery,
	useMutation as _useMutation
} from '@tanstack/react-query'
import { useChainId } from 'wagmi'

export function useQuery<
	TQueryFnData = unknown,
	TError = unknown,
	TData = TQueryFnData,
	TQueryKey extends QueryKey = QueryKey
>({ queryKey, ...options }: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>) {
	const chainId = useChainId()

	return _useQuery<TQueryFnData, TError, TData, TQueryKey>({
		...options,
		queryKey: [chainId, ...queryKey] as unknown as TQueryKey
	})
}

export function useInfiniteQuery<
	TQueryFnData,
	TError = DefaultError,
	TData = InfiniteData<TQueryFnData>,
	TQueryKey extends QueryKey = QueryKey,
	TPageParam = unknown
>({ queryKey, ...options }: UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryFnData, TQueryKey, TPageParam>) {
	const chainId = useChainId()

	return _useInfiniteQuery<TQueryFnData, TError, TData, TQueryKey, TPageParam>({
		...options,
		queryKey: [chainId, ...queryKey] as unknown as TQueryKey
	})
}

export function useMutation<TData = unknown, TError = DefaultError, TVariables = void, TContext = unknown>(
	options: UseMutationOptions<TData, TError, TVariables, TContext>
) {
	const chainId = useChainId()

	return _useMutation<TData, TError, TVariables, TContext>({
		...options,
		mutationKey: [chainId, ...(options.mutationKey ?? [])] as unknown as MutationKey
	})
}
